/**
 * View Modal Component
 *
 * Purpose: Modal form for creating and editing views with full field-view junction table support.
 *          Handles view configuration, field assignment with display properties, ordering, and subcontainer grouping.
 *
 * Architecture: Multi-section form modal with field assignment panel featuring display property configuration.
 *              Supports the new field_views junction table architecture with all display properties.
 *
 * Dependencies: React, Material-UI, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 * Updated: 2025-01-27 (Junction table support)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Autocomplete,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Divider,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  RemoveCircle as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
  Sort as SortableIcon,
  FilterList as FilterableIcon,
  TableRows as GroupableIcon,
  ViewColumn as ResizableIcon,
  Label as LabelIcon,
  Folder as FolderIcon,
  KeyboardDoubleArrowRight as AssignAllIcon,
  KeyboardDoubleArrowLeft as UnassignAllIcon,
  Edit as EditOverrideIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

import DescriptionField, { descriptionLengthError } from '../../common/DescriptionField';
import metadataService, {
  View,
  ViewFormData,
  Field,
  Model,
  TenantInfo,
  FieldViewCreate,
} from '../../../services/metadataService';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';

// =============================================================================
// TYPES
// =============================================================================

interface AssignedFieldWithProps extends Omit<Field, 'scope_info'> {
  // Display properties from junction table
  display_order: number;
  is_visible: boolean;
  is_sortable: boolean;
  is_filterable: boolean;
  is_groupable: boolean;
  is_resizable: boolean;
  header_label_override?: string | null;
  display_subcontainer?: string | null;
  // Scope info for multi-tenant override support (compatible with ScopeInfo from metadataService)
  scope_info?: {
    scope: 'GLOBAL' | 'OVERRIDDEN' | 'LOCAL';
    is_override: boolean;
    parent_tenant_id?: string | null;
  };
}

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const FieldAssignmentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  height: '500px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    height: 'auto',
  },
}));

const FieldListPanel = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const PanelContent = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

const FieldCard = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: `1px solid transparent`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const AssignedFieldCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
  margin: theme.spacing(1),
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  border: `1px solid ${isExpanded ? theme.palette.primary.main : 'transparent'}`,
}));

const FieldCardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const FieldCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 1.5, 1.5),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
}));

const PropertyToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const SubcontainerChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  color: theme.palette.info.dark,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: theme.palette.info.main,
  },
}));

// Scope badge for displaying GLOBAL/OVERRIDDEN/LOCAL status
const ScopeBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'scopeType',
})<{ scopeType?: 'GLOBAL' | 'OVERRIDDEN' | 'LOCAL' }>(({ theme, scopeType }) => {
  const colors = {
    GLOBAL: {
      bg: alpha(theme.palette.primary.main, 0.1),
      text: theme.palette.primary.dark,
      border: alpha(theme.palette.primary.main, 0.3),
    },
    OVERRIDDEN: {
      bg: alpha(theme.palette.warning.main, 0.1),
      text: theme.palette.warning.dark,
      border: alpha(theme.palette.warning.main, 0.3),
    },
    LOCAL: {
      bg: alpha(theme.palette.success.main, 0.1),
      text: theme.palette.success.dark,
      border: alpha(theme.palette.success.main, 0.3),
    },
  };
  // Default to LOCAL if scopeType is undefined or invalid
  const validScopeType = scopeType && colors[scopeType] ? scopeType : 'LOCAL';
  const colorConfig = colors[validScopeType];
  return {
    backgroundColor: colorConfig.bg,
    color: colorConfig.text,
    border: `1px solid ${colorConfig.border}`,
    fontWeight: 500,
    fontSize: '0.65rem',
    height: 20,
    '& .MuiChip-label': {
      padding: '0 6px',
    },
    '& .MuiChip-icon': {
      color: colorConfig.text,
      fontSize: '0.875rem',
    },
  };
});

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generate view_id from label using snake_case
 */
const generateViewId = (label: string, modelId: string): string => {
  if (!label.trim()) return '';

  // Convert to lowercase, replace spaces and special chars with underscores
  const sanitized = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .replace(/_+/g, '_'); // Collapse multiple underscores

  // Prepend model_id for uniqueness
  const modelPrefix = modelId
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  return modelPrefix ? `${modelPrefix}_${sanitized}` : sanitized;
};

// =============================================================================
// COMPONENT INTERFACES
// =============================================================================

export interface ViewModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  view?: View;
  tenantContext: TenantInfo;
  canEdit: boolean;
  onClose: () => void;
  onSave: (data: ViewFormData) => Promise<void>;
  onDelete?: (view: View) => Promise<void>;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ViewModal: React.FC<ViewModalProps> = ({
  open,
  mode,
  view,
  tenantContext,
  canEdit,
  onClose,
  onSave,
  onDelete,
}) => {
  // Form state
  const [formData, setFormData] = useState<ViewFormData>({
    view_id: '',
    model_id: '',
    view_type: 'table',
    label: '',
    description: null,
    tenant_id: '',
    fields: [],
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [models, setModels] = useState<{ group: string; options: Model[] }[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [assignedFields, setAssignedFields] = useState<AssignedFieldWithProps[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);
  const [availableSubcontainers, setAvailableSubcontainers] = useState<string[]>([]);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Load dependencies (models)
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoadingDependencies(true);
        const [normalModels, fielddefModels] = await Promise.all([
          metadataService.getNormalModels(),
          metadataService.getFieldDefinitionModels(),
        ]);

        const groupedModels = [
          {
            group: 'Business Models',
            options: (normalModels || []).sort((a, b) => a.name.localeCompare(b.name)),
          },
          {
            group: 'Field Definition Models',
            options: (fielddefModels || []).sort((a, b) => a.name.localeCompare(b.name)),
          },
        ];
        setModels(groupedModels);
      } catch (error) {
        console.error('Failed to load dependencies:', error);
      } finally {
        setLoadingDependencies(false);
      }
    };

    if (open) {
      loadDependencies();
    }
  }, [open]);

  // Load available fields when model is selected
  useEffect(() => {
    const loadFields = async () => {
      if (formData.model_id && tenantContext.tenant_id) {
        try {
          const response = await metadataService.getFields({
            tenant_id: tenantContext.tenant_id,
            model_id: formData.model_id,
          });
          const fields = response || [];
          const sortedFields = fields.sort((a, b) => {
            const labelA = a.label_override || a.field_definition_label || a.fielddef_id;
            const labelB = b.label_override || b.field_definition_label || b.fielddef_id;
            return labelA.localeCompare(labelB);
          });
          setAvailableFields(sortedFields);
        } catch (error) {
          console.error('Failed to load fields:', error);
        }
      }
    };

    loadFields();
  }, [formData.model_id, tenantContext.tenant_id]);

  // Load existing field assignments when editing
  useEffect(() => {
    const loadAssignedFields = async () => {
      if (mode === 'edit' && view && view.model_id && tenantContext.tenant_id) {
        try {
          // Pass view_tenant_id and current_tenant_id for proper scope resolution
          const viewFields = await metadataService.getViewFields(
            view.view_id,
            view.tenant_id || 'GLOBAL',
            tenantContext.tenant_id,
            false // includeHidden
          );
          const sortedViewFields = viewFields.sort((a, b) => a.display_order - b.display_order);

          const assignedFields: AssignedFieldWithProps[] = sortedViewFields.map((vf, index) => ({
            field_id: vf.field_id || `${vf.fielddef_id}_${vf.model_id}`, // Fallback composite ID
            fielddef_id: vf.fielddef_id,
            tenant_id: vf.tenant_id || '',
            model_id: vf.model_id || view.model_id,
            storage_type: vf.storage_type || 'metadata',
            label_override: vf.label_override,
            field_definition_label: vf.field_label || vf.label,
            is_required: vf.is_required ?? false,
            is_multiselect: vf.is_multiselect ?? false,
            created_at: '',
            updated_at: '',
            created_by: '',
            updated_by: '',
            datatype: vf.field_datatype || vf.datatype || 'text',
            field_definition: vf.field_definition
              ? {
                  datatype: vf.field_definition.datatype as
                    | 'text'
                    | 'list'
                    | 'decimal'
                    | 'date'
                    | 'percent'
                    | 'currency'
                    | 'bool',
                  label: vf.field_definition.label,
                }
              : undefined,
            // Junction table properties
            display_order: vf.display_order ?? index,
            is_visible: vf.is_visible ?? true,
            is_sortable: vf.is_sortable ?? true,
            is_filterable: vf.is_filterable ?? true,
            is_groupable: vf.is_groupable ?? false,
            is_resizable: vf.is_resizable ?? true,
            header_label_override: vf.header_label_override,
            display_subcontainer: vf.display_subcontainer,
            // Scope info for multi-tenant support - convert ScopeInfo to compatible format
            scope_info: vf.scope_info
              ? {
                  scope: vf.scope_info.scope === 'OVERRIDDEN' ? 'OVERRIDDEN' : vf.scope_info.scope,
                  is_override: vf.scope_info.is_override,
                  parent_tenant_id: vf.scope_info.parent_tenant_id,
                }
              : undefined,
          }));

          setAssignedFields(assignedFields);

          // Extract unique subcontainers for autocomplete
          const subcontainers = [
            ...new Set(
              assignedFields.map((f) => f.display_subcontainer).filter((s): s is string => !!s)
            ),
          ];
          setAvailableSubcontainers(subcontainers);
        } catch (error) {
          console.error('Failed to load assigned fields:', error);
        }
      }
    };

    loadAssignedFields();
  }, [mode, view, tenantContext.tenant_id]);

  // Initialize form data when modal opens or mode changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && view) {
        setFormData({
          view_id: view.view_id,
          model_id: view.model_id,
          view_type: (view.view_type || 'table') as 'table' | 'edit-modal' | 'export',
          label: view.label,
          description: view.description || null,
          tenant_id: view.tenant_id || tenantContext.tenant_id,
          fields: [],
        });
      } else if (mode === 'create') {
        // Always reset form data when creating a new view
        setFormData({
          view_id: '',
          model_id: '',
          view_type: 'table',
          label: '',
          description: null,
          tenant_id: tenantContext.tenant_id,
          fields: [],
        });
        setAssignedFields([]);
        setAvailableSubcontainers([]);
      }
      setExpandedFieldId(null);
      setErrors({});
    } else {
      // Reset form data when modal closes to prevent "remembering" old values
      setFormData({
        view_id: '',
        model_id: '',
        view_type: 'table',
        label: '',
        description: null,
        tenant_id: tenantContext.tenant_id,
        fields: [],
      });
      setAssignedFields([]);
      setAvailableSubcontainers([]);
      setExpandedFieldId(null);
      setErrors({});
    }
  }, [open, mode, view, tenantContext.tenant_id]);

  // Auto-generate view_id from label
  useEffect(() => {
    if (mode === 'create' && formData.label && formData.model_id) {
      const generatedId = generateViewId(formData.label, formData.model_id);
      setFormData((prev) => ({ ...prev, view_id: generatedId }));
    }
  }, [formData.label, formData.model_id, mode]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode !== 'edit') {
      if (!formData.view_id.trim()) {
        newErrors.view_id = 'View ID is required';
      } else if (!/^[a-z][a-z0-9]*(_[a-z0-9]+)*$/.test(formData.view_id)) {
        newErrors.view_id = 'View ID must use snake_case format';
      }
    }

    if (!formData.model_id.trim()) {
      newErrors.model_id = 'Model is required';
    }

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    const descError = descriptionLengthError(formData.description);
    if (descError) newErrors.description = descError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, mode]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Convert assigned fields to FieldViewCreate format (using logical field references)
      // IMPORTANT: tenant_id here refers to the FIELD's tenant_id, not the view's tenant_id
      // This allows field-view relationships to reference both GLOBAL and tenant-specific fields
      const fieldViewData: FieldViewCreate[] = assignedFields.map((field, index) => ({
        fielddef_id: field.fielddef_id,
        model_id: field.model_id,
        tenant_id: field.tenant_id || 'GLOBAL', // Use field's tenant_id to reference the correct field instance
        display_order: index,
        is_visible: field.is_visible,
        is_sortable: field.is_sortable,
        is_filterable: field.is_filterable,
        is_groupable: field.is_groupable,
        is_resizable: field.is_resizable,
        header_label_override: field.header_label_override || null,
        display_subcontainer: field.display_subcontainer || null,
      }));

      const submitData: ViewFormData = {
        ...formData,
        fields: fieldViewData,
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to save view:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!view || !onDelete) return;

    try {
      setLoading(true);
      await onDelete(view);
      onClose();
    } catch (error) {
      console.error('Failed to delete view:', error);
    } finally {
      setLoading(false);
    }
  };

  // Field assignment handlers
  const handleAssignField = useCallback(
    (field: Field) => {
      if (!assignedFields.find((f) => f.field_id === field.field_id)) {
        const newField: AssignedFieldWithProps = {
          ...field,
          display_order: assignedFields.length,
          is_visible: true,
          is_sortable: true,
          is_filterable: true,
          is_groupable: false,
          is_resizable: true,
          header_label_override: null,
          display_subcontainer: null,
          // Preserve scope_info if it exists, otherwise default to LOCAL for tenant-specific fields
          scope_info: field.scope_info
            ? {
                scope:
                  field.scope_info.scope === 'Overridden'
                    ? 'OVERRIDDEN'
                    : field.scope_info.scope === 'GLOBAL'
                      ? 'GLOBAL'
                      : 'LOCAL',
                is_override: field.scope_info.is_override,
                parent_tenant_id: ('parent_tenant_id' in field.scope_info
                  ? field.scope_info.parent_tenant_id
                  : undefined) as string | null | undefined,
              }
            : {
                scope: tenantContext.tenant_id === 'GLOBAL' ? 'GLOBAL' : 'LOCAL',
                is_override: false,
              },
        };
        setAssignedFields((prev) => [...prev, newField]);
      }
    },
    [assignedFields, tenantContext.tenant_id]
  );

  const handleAssignAllFields = useCallback(() => {
    const unassignedFields = availableFields.filter(
      (field) => !assignedFields.find((af) => af.field_id === field.field_id)
    );

    const newFields: AssignedFieldWithProps[] = unassignedFields.map((field, index) => ({
      ...field,
      display_order: assignedFields.length + index,
      is_visible: true,
      is_sortable: true,
      is_filterable: true,
      is_groupable: false,
      is_resizable: true,
      header_label_override: null,
      display_subcontainer: null,
      // Preserve scope_info if it exists, otherwise default to LOCAL for tenant-specific fields
      scope_info: field.scope_info
        ? {
            scope:
              field.scope_info.scope === 'Overridden'
                ? 'OVERRIDDEN'
                : field.scope_info.scope === 'GLOBAL'
                  ? 'GLOBAL'
                  : 'LOCAL',
            is_override: field.scope_info.is_override,
            parent_tenant_id: ('parent_tenant_id' in field.scope_info
              ? field.scope_info.parent_tenant_id
              : undefined) as string | null | undefined,
          }
        : {
            scope: tenantContext.tenant_id === 'GLOBAL' ? 'GLOBAL' : 'LOCAL',
            is_override: false,
          },
    }));

    setAssignedFields((prev) => [...prev, ...newFields]);
  }, [availableFields, assignedFields, tenantContext.tenant_id]);

  const handleUnassignField = useCallback(
    (fieldId: string) => {
      setAssignedFields((prev) => prev.filter((f) => f.field_id !== fieldId));
      if (expandedFieldId === fieldId) {
        setExpandedFieldId(null);
      }
    },
    [expandedFieldId]
  );

  const handleUnassignAllFields = useCallback(() => {
    setAssignedFields([]);
    setExpandedFieldId(null);
  }, []);

  const handleReorderField = useCallback(
    (fieldId: string, direction: 'up' | 'down') => {
      const currentIndex = assignedFields.findIndex((f) => f.field_id === fieldId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= assignedFields.length) return;

      const newAssignedFields = [...assignedFields];
      [newAssignedFields[currentIndex], newAssignedFields[newIndex]] = [
        newAssignedFields[newIndex],
        newAssignedFields[currentIndex],
      ];

      // Update display_order
      newAssignedFields.forEach((f, i) => {
        f.display_order = i;
      });

      setAssignedFields(newAssignedFields);
    },
    [assignedFields]
  );

  const handleUpdateFieldProperty = useCallback(
    <K extends keyof AssignedFieldWithProps>(
      fieldId: string,
      property: K,
      value: AssignedFieldWithProps[K]
    ) => {
      setAssignedFields((prev) =>
        prev.map((f) => (f.field_id === fieldId ? { ...f, [property]: value } : f))
      );

      // Track new subcontainers
      if (property === 'display_subcontainer' && value && typeof value === 'string') {
        setAvailableSubcontainers((prev) => (prev.includes(value) ? prev : [...prev, value]));
      }
    },
    []
  );

  const handleToggleExpand = useCallback((fieldId: string) => {
    setExpandedFieldId((prev) => (prev === fieldId ? null : fieldId));
  }, []);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const unassignedFields = useMemo(
    () =>
      availableFields.filter(
        (field) => !assignedFields.find((af) => af.field_id === field.field_id)
      ),
    [availableFields, assignedFields]
  );

  const hiddenFieldsCount = useMemo(
    () => assignedFields.filter((f) => !f.is_visible).length,
    [assignedFields]
  );

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderAvailableField = (field: Field) => {
    const label = field.label_override || field.field_definition_label || field.fielddef_id;
    const datatype = field.field_definition?.datatype || field.datatype || 'text';

    return (
      <FieldCard key={field.field_id} elevation={0} onClick={() => handleAssignField(field)}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {label}
            </Typography>
            <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
              <Chip
                label={metadataService.getDatatypeLabel(datatype)}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
              {field.is_required && (
                <Chip
                  label="Required"
                  size="small"
                  color="warning"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>
          </Box>
          <IconButton size="small" color="primary">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </FieldCard>
    );
  };

  const renderAssignedField = (field: AssignedFieldWithProps, index: number) => {
    const label = field.label_override || field.field_definition_label || field.fielddef_id;
    const datatype = field.field_definition?.datatype || field.datatype || 'text';
    const isExpanded = expandedFieldId === field.field_id;
    const displayLabel = field.header_label_override || label;

    return (
      <AssignedFieldCard key={field.field_id} elevation={0} isExpanded={isExpanded}>
        <FieldCardHeader onClick={() => handleToggleExpand(field.field_id)}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          >
            {index + 1}
          </Box>

          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {displayLabel}
              </Typography>
              {!field.is_visible && (
                <Tooltip title="Hidden by default (available in column selector)">
                  <HiddenIcon fontSize="small" color="action" />
                </Tooltip>
              )}
              {field.display_subcontainer && (
                <SubcontainerChip
                  icon={<FolderIcon />}
                  label={field.display_subcontainer}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              )}
            </Box>
            <Box display="flex" gap={0.5} mt={0.5}>
              <Chip
                label={metadataService.getDatatypeLabel(datatype)}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
              {/* Scope badge for multi-tenant override support */}
              {field.scope_info && field.scope_info.scope && (
                <Tooltip
                  title={
                    field.scope_info.scope === 'GLOBAL'
                      ? 'Global configuration - affects all tenants'
                      : field.scope_info.scope === 'OVERRIDDEN'
                        ? 'Overrides global configuration for this tenant'
                        : 'Tenant-specific configuration'
                  }
                >
                  <ScopeBadge
                    scopeType={field.scope_info.scope}
                    label={field.scope_info.scope}
                    size="small"
                    icon={
                      field.scope_info.scope === 'GLOBAL' ? (
                        <GlobalIcon />
                      ) : field.scope_info.scope === 'OVERRIDDEN' ? (
                        <EditOverrideIcon />
                      ) : (
                        <TenantIcon />
                      )
                    }
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleReorderField(field.field_id, 'up');
              }}
              disabled={index === 0}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleReorderField(field.field_id, 'down');
              }}
              disabled={index === assignedFields.length - 1}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleUnassignField(field.field_id);
              }}
              color="error"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <ExpandMoreIcon
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        </FieldCardHeader>

        <Collapse in={isExpanded}>
          <FieldCardContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Display Properties Row */}
              <Grid size={12}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Display Properties
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Tooltip title="Visible in view">
                    <PropertyToggle
                      onClick={() =>
                        handleUpdateFieldProperty(field.field_id, 'is_visible', !field.is_visible)
                      }
                      sx={{
                        backgroundColor: field.is_visible
                          ? 'success.light'
                          : 'action.disabledBackground',
                        color: field.is_visible ? 'success.contrastText' : 'text.disabled',
                      }}
                    >
                      <VisibleIcon fontSize="small" />
                      <Typography variant="caption">Visible</Typography>
                    </PropertyToggle>
                  </Tooltip>

                  <Tooltip title="Column can be sorted">
                    <PropertyToggle
                      onClick={() =>
                        handleUpdateFieldProperty(field.field_id, 'is_sortable', !field.is_sortable)
                      }
                      sx={{
                        backgroundColor: field.is_sortable
                          ? 'info.light'
                          : 'action.disabledBackground',
                        color: field.is_sortable ? 'info.contrastText' : 'text.disabled',
                      }}
                    >
                      <SortableIcon fontSize="small" />
                      <Typography variant="caption">Sortable</Typography>
                    </PropertyToggle>
                  </Tooltip>

                  <Tooltip title="Column can be filtered">
                    <PropertyToggle
                      onClick={() =>
                        handleUpdateFieldProperty(
                          field.field_id,
                          'is_filterable',
                          !field.is_filterable
                        )
                      }
                      sx={{
                        backgroundColor: field.is_filterable
                          ? 'info.light'
                          : 'action.disabledBackground',
                        color: field.is_filterable ? 'info.contrastText' : 'text.disabled',
                      }}
                    >
                      <FilterableIcon fontSize="small" />
                      <Typography variant="caption">Filterable</Typography>
                    </PropertyToggle>
                  </Tooltip>

                  <Tooltip title="Column can be grouped by">
                    <PropertyToggle
                      onClick={() =>
                        handleUpdateFieldProperty(
                          field.field_id,
                          'is_groupable',
                          !field.is_groupable
                        )
                      }
                      sx={{
                        backgroundColor: field.is_groupable
                          ? 'warning.light'
                          : 'action.disabledBackground',
                        color: field.is_groupable ? 'warning.contrastText' : 'text.disabled',
                      }}
                    >
                      <GroupableIcon fontSize="small" />
                      <Typography variant="caption">Groupable</Typography>
                    </PropertyToggle>
                  </Tooltip>

                  <Tooltip title="Column width can be resized">
                    <PropertyToggle
                      onClick={() =>
                        handleUpdateFieldProperty(
                          field.field_id,
                          'is_resizable',
                          !field.is_resizable
                        )
                      }
                      sx={{
                        backgroundColor: field.is_resizable
                          ? 'info.light'
                          : 'action.disabledBackground',
                        color: field.is_resizable ? 'info.contrastText' : 'text.disabled',
                      }}
                    >
                      <ResizableIcon fontSize="small" />
                      <Typography variant="caption">Resizable</Typography>
                    </PropertyToggle>
                  </Tooltip>
                </Box>
              </Grid>

              {/* Label Override */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Header Label Override"
                  placeholder={label}
                  value={field.header_label_override || ''}
                  onChange={(e) =>
                    handleUpdateFieldProperty(
                      field.field_id,
                      'header_label_override',
                      e.target.value || null
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <LabelIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                  helperText="Custom column header for this view"
                />
              </Grid>

              {/* Subcontainer */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small"
                  freeSolo
                  options={availableSubcontainers}
                  value={field.display_subcontainer || ''}
                  onChange={(_, newValue) =>
                    handleUpdateFieldProperty(
                      field.field_id,
                      'display_subcontainer',
                      newValue || null
                    )
                  }
                  onInputChange={(_, newValue) =>
                    handleUpdateFieldProperty(
                      field.field_id,
                      'display_subcontainer',
                      newValue || null
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subcontainer / Section"
                      placeholder="e.g., Basic Info, Settings"
                      helperText="Group fields into sections"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <FolderIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FieldCardContent>
        </Collapse>
      </AssignedFieldCard>
    );
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create View' : 'Edit View'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {loadingDependencies ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Basic Info Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Display Label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  error={!!errors.label}
                  helperText={errors.label || 'Human-readable name for this view'}
                  fullWidth
                  disabled={!canEdit}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="View ID"
                  value={formData.view_id}
                  onChange={(e) => setFormData({ ...formData, view_id: e.target.value })}
                  error={!!errors.view_id}
                  helperText={errors.view_id || 'Auto-generated from label (snake_case)'}
                  fullWidth
                  disabled={mode === 'edit' || !canEdit}
                  InputProps={{
                    readOnly: mode === 'create',
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: mode === 'create' ? 'text.secondary' : undefined,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={models.flatMap((group) => group.options)}
                  groupBy={(option) => {
                    const group = models.find((g) => g.options.includes(option));
                    return group?.group || '';
                  }}
                  getOptionLabel={(option) => option.name}
                  value={
                    models
                      .flatMap((group) => group.options)
                      .find((m) => m.model_id === formData.model_id) || null
                  }
                  onChange={(_event, newValue) => {
                    const newModelId = newValue?.model_id || '';
                    if (newModelId !== formData.model_id && assignedFields.length > 0) {
                      setAssignedFields([]);
                    }
                    setFormData({ ...formData, model_id: newModelId });
                  }}
                  disabled={!canEdit || mode === 'edit'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Model"
                      required
                      error={!!errors.model_id}
                      helperText={errors.model_id || 'Select the model for this view'}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth disabled={!canEdit || mode === 'edit'}>
                  <InputLabel>View Type *</InputLabel>
                  <Select
                    value={formData.view_type}
                    onChange={(e: SelectChangeEvent) =>
                      setFormData({
                        ...formData,
                        view_type: e.target.value as 'table' | 'edit-modal' | 'export',
                      })
                    }
                    label="View Type *"
                    disabled={mode === 'edit'}
                  >
                    <MenuItem value="table">Table View</MenuItem>
                    <MenuItem value="edit-modal">Edit Modal / Form</MenuItem>
                    <MenuItem value="export">Export View</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <DescriptionField
                  label="Description"
                  value={formData.description || ''}
                  onChange={(value) => setFormData({ ...formData, description: value || null })}
                  fullWidth
                  disabled={!canEdit}
                  error={!!errors.description}
                  helperText={errors.description || "Optional description of this view's purpose"}
                />
              </Grid>
            </Grid>

            {/* Field Assignment Section */}
            {formData.model_id && canEdit && (
              <>
                <Divider sx={{ my: 2 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">Field Assignment</Typography>
                  {hiddenFieldsCount > 0 && (
                    <Tooltip title="Fields hidden by default (available in column selector)">
                      <Chip
                        icon={<HiddenIcon />}
                        label={`${hiddenFieldsCount} hidden by default`}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Box>

                <FieldAssignmentSection>
                  {/* Available Fields Panel */}
                  <FieldListPanel elevation={0}>
                    <PanelHeader>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Available Fields ({unassignedFields.length})
                      </Typography>
                      {unassignedFields.length > 0 && (
                        <Tooltip title="Assign all fields">
                          <IconButton size="small" onClick={handleAssignAllFields}>
                            <AssignAllIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </PanelHeader>
                    <PanelContent>
                      {unassignedFields.length === 0 ? (
                        <Box p={3} textAlign="center">
                          <Typography color="textSecondary" variant="body2">
                            All fields have been assigned
                          </Typography>
                        </Box>
                      ) : (
                        unassignedFields.map(renderAvailableField)
                      )}
                    </PanelContent>
                  </FieldListPanel>

                  {/* Assigned Fields Panel */}
                  <FieldListPanel elevation={0}>
                    <PanelHeader>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Assigned Fields ({assignedFields.length})
                      </Typography>
                      {assignedFields.length > 0 && (
                        <Tooltip title="Remove all fields">
                          <IconButton size="small" onClick={handleUnassignAllFields} color="error">
                            <UnassignAllIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </PanelHeader>
                    <PanelContent>
                      {assignedFields.length === 0 ? (
                        <Box p={3} textAlign="center">
                          <Typography color="textSecondary" variant="body2">
                            Click fields to assign them to this view
                          </Typography>
                          <Typography
                            color="textSecondary"
                            variant="caption"
                            display="block"
                            mt={1}
                          >
                            Expand assigned fields to configure display properties
                          </Typography>
                        </Box>
                      ) : (
                        assignedFields.map((field, index) => renderAssignedField(field, index))
                      )}
                    </PanelContent>
                  </FieldListPanel>
                </FieldAssignmentSection>

                {assignedFields.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Tip:</strong> Click on an assigned field to expand and configure its
                      display properties (visibility, sorting, filtering, custom labels, and
                      subcontainer grouping).
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          canEdit && (
            <DestructiveButton
              onClick={handleDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>

        {canEdit && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingDependencies}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create View' : 'Update View'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default ViewModal;
